import { subYears, isPast, isValid, endOfDay } from 'date-fns';
import { every } from 'lodash';
import Yup from './customYupValidationMethods';
import { getYearsAgo } from '../helpers/dates';
import { isBankAccountValid, isBankValid } from './bankAccountValidations';
import i18n from '../i18n';
import { getPassportsValidation } from '../helpers/armenianPassportsValidationHelper';
import { uniquePassportValidator, uniqueSocCardValidator } from './uniqueItemsValidator';
import { isArmenianCountry } from '../helpers/countryHelper';
import { isArmenianLanguage } from '../helpers/langHelpers';
import {
  EMAIL_VALIDATION_PATTERN,
  MATCHES_STRINGS_WITH_ZERO_OR_MORE_DIGITS,
  ONLY_DIGITS_REGEX_ALT,
} from '../constants/patterns';
import { TRAVEL_MAX_AGE } from '../constants/travelCalcConstants';
import { GENDER, MIN_USER_AGE } from '../constants';

export const isOnlyArmenianLetters = (text) => text
  && typeof text === 'string'
  && every(
    text.split(''),
    (item) => 'աբգդեզէըթժիլխծկհձղճմյնշոչպջռսվտրցւփքօֆևԱԲԳԴԵԶԷԸԹԺԻԼԽԾԿՀՁՂՃՄՅՆՇՈՉՊՋՌՍՎՏՐՑՒՓՔՕՖ1234567890_. ։:,()/\\-․֊«»<>―՞?՝`~՜՛\'"'.indexOf(
      item,
    ) !== -1,
  );

// this validator commonly used in claims form, when citizenship is armenian
export function getSocCardValidation(citizenship, schema) {
  if (isArmenianCountry(citizenship)) {
    return schema
      .test('socialCard', 'validationErrors.socialCardWrongNumber', (value) => {
        if (value) {
          return !(value.length !== 10 || !MATCHES_STRINGS_WITH_ZERO_OR_MORE_DIGITS.test(value));
        }
        return schema;
      }).required('validationErrors.isRequired');
  }
  return schema;
}

export const englishStreetFieldValidations = Yup.object().shape({
  streetEn: Yup.string()
    .matches(/^[a-zA-Z0-9_. ,()/-]*$/, 'validationErrors.onlyEnglish')
    .when([], {
      is: () => isArmenianLanguage(i18n.language),
      otherwise: Yup.string().required('validationErrors.isRequired'),
    }),
});

export const armCellPhoneNumberPrefixes = [
  '033',
  '041',
  '043',
  '044',
  '049',
  '055',
  '077',
  '091',
  '093',
  '094',
  '095',
  '096',
  '097',
  '098',
  '099',
];

export const armPhoneNumberPrefixes = ['010', '011', '012', '060'];

export const armRegionalPhoneNumberPrefixes = [
  '0223',
  '0224',
  '0226',
  '0231',
  '0232',
  '0233',
  '0234',
  '0235',
  '0236',
  '0237',
  '0242',
  '0244',
  '0245',
  '0246',
  '0249',
  '0252',
  '0253',
  '0254',
  '0255',
  '0256',
  '0257',
  '0261',
  '0262',
  '0263',
  '0264',
  '0265',
  '0266',
  '0267',
  '0268',
  '0269',
  '0281',
  '0282',
  '0283',
  '0284',
  '0285',
  '0286',
  '0287',
  '0312',
  '0322',
  '0471',
  '0474',
  '0475',
  '0476',
  '0477',
  '0478',
];

export const onlyArmenianValidation = Yup.string()
  .test('onlyArmenian', 'validationErrors.onlyArmenian', isOnlyArmenianLetters)
  .required('validationErrors.isRequired');

export const socialCardNumberValidation = Yup.string().when(
  ['citizenship'],
  (citizenship, schema) => {
    if (isArmenianCountry(citizenship)) {
      return schema
        .test('socialCard', 'validationErrors.socialCardWrongNumber', (value) => {
          if (value) {
            return !(value.length !== 10 || !MATCHES_STRINGS_WITH_ZERO_OR_MORE_DIGITS.test(value));
          }
          return true;
        })
        .when(['birthday'], (birthday, sch) => {
          if (birthday && birthday < subYears(new Date(), MIN_USER_AGE)) {
            return sch.required('validationErrors.isRequired');
          }
          return sch;
        });
    }
    return schema;
  },
);

export const emailSchema = Yup.object().shape({
  email: Yup.string()
    .matches(EMAIL_VALIDATION_PATTERN, 'validationErrors.invalidEmail')
    .required('validationErrors.isRequired'),
  emailConfirm: Yup.string()
    .oneOf([Yup.ref('email'), ''], 'validationErrors.emailsDoNotMatch')
    .required('validationErrors.isRequired'),
});

export const passwordSchema = Yup.object().shape({
  password: Yup.string()
    .matches(/(?=.*[A-Z])/, 'validationErrors.invalidPasswordMissingUppercase')
    .matches(/(?=.*[a-z])/, 'validationErrors.invalidPasswordMissingLowercase')
    .matches(/(?=.*[0-9])/, 'validationErrors.invalidPasswordMissingDigit')
    .min(8, 'validationErrors.minLength')
    .required('validationErrors.isRequired'),
  repeatPassword: Yup.string()
    .oneOf([Yup.ref('password'), ''], 'validationErrors.passwordsDoNotMatch')
    .required('validationErrors.isRequired'),
});

export const mainUserFormSchema = Yup.object().shape(
  {
    firstName: onlyArmenianValidation,
    lastName: onlyArmenianValidation,
    firstNameEn: Yup.string()
      .matches(/^[a-zA-Z0-9_. ,()/-]*$/, 'validationErrors.onlyEnglish')
      .required('validationErrors.isRequired'),
    lastNameEn: Yup.string()
      .matches(/^[a-zA-Z0-9_. ,()/-]*$/, 'validationErrors.onlyEnglish')
      .required('validationErrors.isRequired'),
    gender: Yup.number()
      .oneOf([GENDER.MALE, GENDER.FEMALE], 'validationErrors.isRequired')
      .required('validationErrors.isRequired')
      .nullable(),
    birthday: Yup.date()
      .max(subYears(new Date(), MIN_USER_AGE), 'validationErrors.minAge')
      .nullable()
      .typeError('validationErrors.invalidDate')
      .required('validationErrors.isRequired'),
    socCard: socialCardNumberValidation,
    citizenship: Yup.object()
      .nullable()
      .required('validationErrors.isRequired'),
    passport: Yup.string().when(['citizenship'], (citizenship, schema, test) => getPassportsValidation(citizenship, schema, test)),
    passportGiven: Yup.date()
      .nullable()
      .max(endOfDay(new Date()), 'validationErrors.passportGivenMessage')
      .typeError('validationErrors.invalidDate')
      .required('validationErrors.isRequired'),
    passportValidUntil: Yup.date()
      .nullable()
      .when(['passportGiven'], (passportGiven, schema) => {
        if (isValid(new Date(passportGiven))) {
          return schema.test(
            'validityDate',
            'validationErrors.validityDate',
            (value) => !value || (value > passportGiven && !isPast(value)),
          );
        }
        return schema.test(
          'validityDate',
          'validationErrors.validityDate',
          (value) => !isPast(value),
        );
      })
      .typeError('validationErrors.invalidDate')
      .required('validationErrors.isRequired'),
    passportFrom: Yup.string()
      .when(['citizenship'], (citizenship, schema) => {
        if (isArmenianCountry(citizenship)) {
          return schema.test('passportFrom', 'validationErrors.wrongIssuer', (value) => ONLY_DIGITS_REGEX_ALT.test(value));
        }
        return schema;
      })
      .required('validationErrors.isRequired'),

    cellPhone: Yup.string()
      .test('cellPhone', 'validationErrors.wrongMobilePhoneNumber.onlyDigits', (value) => ONLY_DIGITS_REGEX_ALT.test(value))
      .when(['citizenship'], (citizenship, schema) => {
        if (isArmenianCountry(citizenship)) {
          return schema
            .test(
              'armenianCellLength',
              'validationErrors.wrongMobilePhoneNumber.length',
              (value = '') => value.length === 0 || value.length === 9,
            )
            .test(
              'armenianCellPrefix',
              'validationErrors.wrongMobilePhoneNumber.code',
              (value = '') => value.length === 0 || armCellPhoneNumberPrefixes.indexOf(value.slice(0, 3)) !== -1,
            );
        }
        return schema;
      })
      .when(['phone'], (phone, schema) => {
        if (!phone) {
          return schema.required('validationErrors.isRequired');
        }
        return schema;
      }),
    phone: Yup.string()
      .test('phoneNumber', 'validationErrors.wrongPhoneNumber.onlyDigits', (value) => ONLY_DIGITS_REGEX_ALT.test(value))
      .when(['citizenship'], (citizenship, schema) => {
        if (isArmenianCountry(citizenship)) {
          return schema
            .test(
              'armenianNumberLength',
              'validationErrors.wrongPhoneNumber.length',
              (value = '') => value.length === 0 || value.length === 9,
            )
            .test(
              'properArmenianPrefix',
              'validationErrors.wrongPhoneNumber.code',
              (value = '') => value.length === 0 || armPhoneNumberPrefixes.indexOf(value.slice(0, 3)) !== -1,
            );
        }
        return schema;
      }),
    country: Yup.object()
      .required('validationErrors.isRequired')
      .nullable(),
    state: Yup.object()
      .nullable()
      .when(['country'], (country, schema) => {
        if (country && isArmenianCountry(country)) {
          return schema.required('validationErrors.isRequired');
        }
        return schema;
      }),
    cityVillage: Yup.object()
      .nullable()
      .when(['country', 'state'], (country, state, schema) => {
        if (country && country.label === 'ARM' && !(state.label && state.label === 'YR')) {
          return schema.required('validationErrors.isRequired');
        }
        return schema;
      }),
    street: Yup.string()
      .test('onlyArmenian', 'validationErrors.onlyArmenian', isOnlyArmenianLetters)
      .required('validationErrors.isRequired'),
  },
  [['cellPhone', 'phone']],
);

export const propertyDataSchema = Yup.object().shape({
  propertyCountry: Yup.object()
    .required('validationErrors.isRequired')
    .nullable(),
  propertyState: Yup.object()
    .nullable()
    .when(['propertyCountry'], (country, schema) => {
      if (country && isArmenianCountry(country)) {
        return schema.required('validationErrors.isRequired');
      }
      return schema;
    }),
  propertyCityVillage: Yup.object()
    .nullable()
    .when(['propertyCountry'], (country, schema) => {
      if (country && isArmenianCountry(country)) {
        return schema.required('validationErrors.isRequired');
      }
      return schema;
    }),
  propertyStreet: Yup.string()
    .test('onlyArmenian', 'validationErrors.onlyArmenian', isOnlyArmenianLetters)
    .required('validationErrors.isRequired'),
  propertyStreetEn: Yup.string().required('validationErrors.isRequired'),
  ownershipNumber: Yup.string().nullable(),
});

export const cmtplDraftSchema = Yup.object()
  .shape(
    {
      useType: Yup.object()
        .required('validationErrors.isRequired')
        .nullable(),
      phone: Yup.string()
        .test(
          'armenianNumberLength',
          'validationErrors.wrongPhoneNumber.length',
          (value = '') => value.length === 0 || value.length === 9,
        )
        .test(
          'properArmenianPrefix',
          'validationErrors.wrongPhoneNumber.code',
          (value = '') => value.length === 0
            || [...armCellPhoneNumberPrefixes, ...armPhoneNumberPrefixes].indexOf(
              value.slice(0, 3),
            ) !== -1
            || armRegionalPhoneNumberPrefixes.indexOf(value.slice(0, 4)) !== -1,
        )
        .test('phoneNumber', 'validationErrors.wrongPhoneNumber.onlyDigits', (value = '') => ONLY_DIGITS_REGEX_ALT.test(value))
        .required('validationErrors.isRequired')
        .nullable(),
      email: Yup.string()
        .matches(EMAIL_VALIDATION_PATTERN, 'validationErrors.invalidEmail')
        .required('validationErrors.isRequired')
        .nullable(),
      emailConfirm: Yup.string()
        .oneOf([Yup.ref('email'), ''], 'validationErrors.emailsDoNotMatch')
        .required('validationErrors.isRequired')
        .nullable(),
      bankAccountNumber: Yup.string().when(
        ['bank', 'aswaBankCodes'],
        (bank, aswaBankCodes, schema) => {
          if (bank) {
            return schema
              .test('isBankValid', 'validationErrors.bankAccountMismatch', (value) => isBankValid(bank, value))
              .test('isBankAccountValid', 'validationErrors.invalidBankAccount', (value) => isBankAccountValid(value, aswaBankCodes))
              .test('isOnlyDigit', 'validationErrors.onlyDigits', (value) => ONLY_DIGITS_REGEX_ALT.test(value))
              .required('validationErrors.isRequired')
              .nullable();
          }
          return schema.nullable();
        },
      ),
      bank: Yup.object().when(['bankAccountNumber'], (bankAccountNumber, schema) => {
        if (bankAccountNumber) {
          return schema.required('validationErrors.isRequired').nullable();
        }
        return schema.nullable();
      }),
      country: Yup.object()
        .required('validationErrors.isRequired')
        .nullable(),
      state: Yup.object()
        .nullable()
        .when(['country'], (country, schema) => {
          if (country && isArmenianCountry(country)) {
            return schema.required('validationErrors.isRequired');
          }
          return schema;
        }),
      cityVillage: Yup.object()
        .nullable()
        .when(['country'], (country, schema) => {
          if (country && isArmenianCountry(country)) {
            return schema.required('validationErrors.isRequired');
          }
          return schema;
        }),
      street: Yup.string()
        .test('onlyArmenian', 'validationErrors.onlyArmenian', isOnlyArmenianLetters)
        .required('validationErrors.isRequired'),
      firstName: onlyArmenianValidation,
      lastName: onlyArmenianValidation,
      firstNameEn: Yup.string()
        .matches(/^[a-zA-Z0-9_. ,()/-]*$/, 'validationErrors.onlyEnglish')
        .required('validationErrors.isRequired'),
      lastNameEn: Yup.string()
        .matches(/^[a-zA-Z0-9_. ,()/-]*$/, 'validationErrors.onlyEnglish')
        .required('validationErrors.isRequired'),
    },
    ['bank', 'bankAccountNumber'],
  )
  .concat(englishStreetFieldValidations);

export const vehicleSearchSchema = Yup.object().shape({
  plateNum: Yup.string()
    .required('validationErrors.isRequired')
    .nullable(),
  idNumber: Yup.string()
    .required('validationErrors.isRequired')
    .nullable(),
  hasAgreedToSharePersonalData: Yup.boolean().oneOf([true]),
});

export const registrationSchema = mainUserFormSchema.concat(emailSchema).concat(passwordSchema);

export const contractSignerSchema = mainUserFormSchema
  .concat(emailSchema)
  .concat(englishStreetFieldValidations);

export const memberSchema = mainUserFormSchema
  .concat(
    Yup.object().shape({
      birthday: Yup.date()
        .max(new Date(), 'validationErrors.wrongBirthday')
        .min(getYearsAgo(TRAVEL_MAX_AGE + 1), 'validationErrors.maxAge')
        .nullable()
        .typeError('validationErrors.invalidDate')
        .required('validationErrors.isRequired'),
    }),
  )
  .concat(englishStreetFieldValidations);

export const newMemberSchema = memberSchema.concat(
  Yup.object().shape({
    passport: Yup.reach(mainUserFormSchema, 'passport').test(
      'duplicatePassport',
      'validationErrors.duplicatePassport',
      uniquePassportValidator.checkIfUnique,
    ),
    socCard: Yup.reach(mainUserFormSchema, 'socCard').test(
      'duplicateSocCard',
      'validationErrors.duplicateSocCard',
      uniqueSocCardValidator.checkIfUnique,
    ),
  }),
);

export const contractMembersSchema = Yup.object().shape({
  members: Yup.array()
    .of(memberSchema)
    .unique('duplicate passport', 'passport')
    .unique('duplicate socCard', 'socCard'),
});

export const physicalEntitySchemaCitizen = Yup.object().shape({
  firstName: Yup.string()
    .required('validationErrors.isRequired')
    .isCorrectLanguage(),
  lastName: Yup.string()
    .required('validationErrors.isRequired')
    .isCorrectLanguage(),
  passport: Yup.reach(mainUserFormSchema, 'passport'),
});

export const physicalEntitySchemaNonCitizen = physicalEntitySchemaCitizen.concat(
  Yup.object().shape({
    passportGiven: Yup.reach(mainUserFormSchema, 'passportGiven'),
    passportValidUntil: Yup.date()
      .nullable()
      .notRequired(),
    passportFrom: Yup.reach(mainUserFormSchema, 'passportFrom'),
  }),
);
